import { Box, Card, CardContent, Chip, Divider, LinearProgress, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { Link, useGetOne, useRecordContext, useTranslate } from 'react-admin';

import {
  GetB2bCompaniesIdEndpoint,
  GetB2bDashboardActiveCampaignsData,
  GetB2bDashboardActiveCampaignsResponse,
} from '@Common/types/bo/b2b/companies';

import { B2bCampaignsCreate } from '@sh4res/bo-common/components/B2bCampaigns/B2bCampaignsCreate';
import { mockActiveCampaigns } from '@sh4res/bo-common/components/B2bCompanyDashboard/mock_data';
import { ResourceBasePath, useResourceBasePath } from '@sh4res/bo-common/hooks';
import { formatCentsNumber, Resources } from '@sh4res/bo-common/utils';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

type ActiveCampaign = GetB2bDashboardActiveCampaignsData[number];

const formatDaysRemaining = (days: number): string => {
  if (days <= 0) {
    return '0d';
  }
  return `${days}d`;
};

const CircularProgress = ({ progress, days }: { progress: number; days: number }) => {
  const progressData = [
    {
      id: 'completed',
      value: progress,
      color: '#4F46E5',
    },
    {
      id: 'remaining',
      value: 1 - progress,
      color: '#e0e0e0',
    },
  ];

  return (
    <Box sx={{ position: 'relative', width: 48, height: 48 }}>
      <PieChart
        series={[
          {
            data: progressData,
            innerRadius: 16,
            outerRadius: 22,
            startAngle: 0,
            endAngle: 360,
            paddingAngle: 0,
            cx: 24,
            cy: 24,
          },
        ]}
        width={48}
        height={48}
        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        slotProps={{
          legend: { hidden: true },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 600,
          color: '#4F46E5',
          pointerEvents: 'none',
        }}>
        {formatDaysRemaining(days)}
      </Box>
    </Box>
  );
};

const CampaignCard = ({
  campaign,
  resource,
  resourceBasePath,
  isFirst,
  translate,
  campaignsCount,
  companyId,
}: {
  campaign: ActiveCampaign;
  resource: Resources;
  resourceBasePath: ResourceBasePath;
  isFirst: boolean;
  translate: ReturnType<typeof useTranslate>;
  campaignsCount: number;
  companyId?: number;
}) => {
  const campaignResource = `${resourceBasePath}campaigns` as const;
  const campaignPath = buildLink(campaignResource, 'show', campaign.campaign_id);

  return (
    <Card sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
      <CardContent sx={{ padding: 0 }}>
        {isFirst && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: '20px',
                pt: '20px',
                pb: '15px',
              }}>
              <Typography variant="h3" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                {translate(
                  `resources.${resource}.dashboard.active_campaigns.title.${campaignsCount === 1 ? 'singular' : 'plural'}`,
                )}
              </Typography>
              <B2bCampaignsCreate
                resource={campaignResource}
                label={translate(`resources.${resource}.dashboard.active_campaigns.create`)}
                variant="outlined"
                size="small"
                companyId={companyId}
                sx={{
                  backgroundColor: 'transparent',
                  color: '#594FF4',
                  borderColor: '#e0e0e0',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  minWidth: 'auto',
                  marginRight: '0 !important',
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#594FF4',
                  },
                  '& .MuiButton-startIcon': {
                    color: '#594FF4',
                  },
                }}
              />
            </Box>
            <Divider />
          </>
        )}

        {/* Header section with Participation and days */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: '20px', py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: '34%' }}>
            {translate(`resources.bo_common.b2b.campaigns.choices.type.${campaign.campaign_type}`)}
          </Typography>
          <Box sx={{ width: '64%', textAlign: 'left' }}>
            <CircularProgress
              progress={campaign.temporal_progress}
              days={campaign.days_remaining}
            />
          </Box>
        </Box>
        <Divider />

        {/* Campaign details */}
        <Box sx={{ display: 'flex', px: '20px', py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: '34%' }}>
            {translate(`resources.${resource}.dashboard.active_campaigns.operation_id`)}
          </Typography>
          <Link
            to={campaignPath}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: '64%',
              textAlign: 'left',
              color: '#4F46E5',
              fontWeight: 500,
              fontSize: '0.875rem',
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}>
            {campaign.campaign_id}
          </Link>
        </Box>
        <Divider />

        <Box sx={{ display: 'flex', px: '20px', py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: '34%' }}>
            {translate(`resources.${resource}.dashboard.active_campaigns.status`)}
          </Typography>
          <Box sx={{ width: '64%', textAlign: 'left' }}>
            <Chip
              label={translate(`resources.${resource}.dashboard.active_campaigns.in_progress`)}
              size="small"
              sx={{
                backgroundColor: '#f5f5f5',
                color: '#666666',
                fontSize: '0.75rem',
                fontWeight: 500,
                height: '24px',
              }}
            />
          </Box>
        </Box>
        <Divider />

        <Box sx={{ display: 'flex', px: '20px', py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: '34%' }}>
            {translate(`resources.${resource}.dashboard.active_campaigns.amount`)}
          </Typography>
          <Typography variant="body2" sx={{ width: '64%', textAlign: 'left', fontWeight: 500 }}>
            {formatCentsNumber(campaign.amount)}
          </Typography>
        </Box>
        <Divider />

        {/* Answer rate section */}
        <Box sx={{ px: '20px', pt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={campaign.answer_rate * 100}
            sx={{
              height: 20,
              borderRadius: 10,
              backgroundColor: '#f5f5f5',
              mb: 1,
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#4F46E5',
                borderRadius: 10,
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {translate(`resources.${resource}.dashboard.active_campaigns.answer_rate`)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(campaign.answer_rate * 100)}%{' '}
              {translate(`resources.${resource}.dashboard.active_campaigns.of_affiliates_answered`)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const ActiveCampaignsSection = ({
  useMock = false,
  resource,
}: {
  useMock?: boolean;
  resource: Resources;
}) => {
  const record = useRecordContext<GetB2bCompaniesIdEndpoint['Client']['ResponseBody']>();
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();

  const { data: realData } = useGetOne<GetB2bDashboardActiveCampaignsResponse>(
    `${resource}/dashboard/active_campaigns`,
    { id: record?.id as number },
    { enabled: !!record && !useMock },
  );

  const campaigns = useMock ? mockActiveCampaigns.data : realData?.data;

  if (!record || !campaigns || campaigns.length === 0) {
    return null;
  }

  return (
    <Box>
      {campaigns.map((campaign: ActiveCampaign, index: number) => (
        <CampaignCard
          key={campaign.campaign_id}
          campaign={campaign}
          resource={resource}
          resourceBasePath={resourceBasePath}
          isFirst={index === 0}
          translate={translate}
          campaignsCount={campaigns.length}
          companyId={record.id}
        />
      ))}
    </Box>
  );
};
